import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GA4_ID = (process.env.REACT_APP_GA4_ID || "").trim();

/* ============================================================
 * dataLayer helpers — every analytics event flows through here.
 * GTM picks this up automatically. GA4 (when enabled) too.
 * ============================================================ */
const ensureDataLayer = () => {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const pushDataLayer = (payload) => {
  const dl = ensureDataLayer();
  if (!dl) return;
  try {
    dl.push(payload);
  } catch (_) { /* noop */ }
};

/* Direct GA4 tag (loads once, lazily). Only activates when REACT_APP_GA4_ID is set. */
let ga4Loaded = false;
export const initGA4 = () => {
  if (typeof window === "undefined") return;
  if (!GA4_ID || ga4Loaded) return;
  ga4Loaded = true;
  try {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { send_page_view: false }); // SPA — we send page_view manually
  } catch (_) { /* noop */ }
};

/* ============================================================
 * Public API
 * ============================================================ */

/** Generic event — fires to backend + dataLayer (GTM/GA4 see it). */
export const trackEvent = (event, extra = {}) => {
  const path = typeof window !== "undefined" ? window.location.pathname : null;

  // 1) Backend
  try {
    axios.post(`${API}/analytics/event`, {
      event,
      page: path,
      registration_id: extra.registration_id || null,
      metadata: extra.metadata || null,
    }).catch(() => {});
  } catch (_) { /* noop */ }

  // 2) dataLayer (GTM + any tags configured inside it)
  pushDataLayer({
    event,
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : null,
    page_title: typeof document !== "undefined" ? document.title : null,
    ...(extra.metadata || {}),
    ...(extra.registration_id ? { registration_id: extra.registration_id } : {}),
  });

  // 3) Direct GA4 (if configured)
  if (GA4_ID && typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", event, {
        page_path: path,
        ...(extra.metadata || {}),
      });
    } catch (_) { /* noop */ }
  }
};

/** Page-view event for SPA route changes. */
export const trackPageView = (path) => {
  pushDataLayer({
    event: "page_view",
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : null,
    page_title: typeof document !== "undefined" ? document.title : null,
  });
  if (GA4_ID && typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    } catch (_) { /* noop */ }
  }
};

/** Conversion event (also sets a normalized GA4 "conversion" hint). */
export const trackConversion = (name, value = null, currency = "USD", extra = {}) => {
  trackEvent(name, {
    metadata: {
      conversion: true,
      value,
      currency,
      ...extra,
    },
  });
};
